// CopyCat (Viral To Me) — viral-script analysis + AI-driven adaptation.
// Takes a pasted TikTok/Reel/Shorts script, identifies the viral mechanics
// that made it work, then minimally rewrites the body so it feels original
// while preserving the hook structure, pacing, and emotional retention curve.
// The CTA is auto-replaced with the user's current CTA Control Panel settings.

import { chatJSON } from "./openai";
import { ctaIsValid } from "./prompts";

const STRENGTH_LABELS = {
  minimal:    "MINIMAL — keep almost every sentence; only swap names, niches, products, and cosmetic details to match the new niche/offer. Sentence cadence is preserved verbatim where possible. Goal: the new script reads like the SAME viral post with niche details swapped.",
  balanced:   "BALANCED — keep the hook structure, retention curve, and ~70% of the original wording / cadence; only rewrite what genuinely needs adapting for the new niche. Preserve every emotional beat, every curiosity gap, every pause. Goal: same psychology, new niche.",
  aggressive: "AGGRESSIVE — keep the underlying viral mechanics (hook archetype, emotional arc, retention triggers, payoff structure) AND the original sentence rhythm; rewrite the body so it lives natively in the new niche but the spine is still detectable on a side-by-side. Goal: feels like the original creator pivoted to your niche.",
};

const HARD_RULES = `You are CopyCat — BuddyPass AI's viral-structure analyser and adapter.

Your job: take a real, proven viral short-form script and adapt it for the user's niche and offer WITHOUT killing what made it work.

PRESERVE-OVER-REWRITE PRINCIPLE (most important rule):
The instinct is to "polish" or "improve" the original. DO NOT. The original is viral because of its imperfections, its rhythm, its real-person tone. Your job is to PRESERVE the viral psychology, not to clean up the language. If you find yourself making a sentence "nicer", "smoother", or "more articulate" — STOP and revert. AI-polished wording, sanitised energy, and excessive explanation are the three failure modes that kill virality. Lean toward UNDER-rewriting.

CORE LAWS (do not break):
1. NEVER claim or imply you wrote the original. The user's input is someone else's viral script — you analyse it, then produce an "inspired" adaptation.
2. Preserve the ORIGINAL HOOK STRUCTURE as closely as the chosen Transformation Strength allows. The first 1-3 seconds of the new script must use the same archetype as the original (curiosity gap / pattern interrupt / contrarian / list / POV / question — whichever the original used).
3. Preserve pacing, sentence cadence, emotional intensity, retention triggers, curiosity gaps, AND personality energy. The new script should feel like the SAME PERSON in the SAME RHYTHM — just talking about the user's niche.
4. The CTA at the END of the new script must be REPLACED with the user's configured CTA verbatim. Keep the CTA placement (final beat) and energy level (high / medium / soft) of the original.
5. NEVER invent assets. The CTA is what the user gives you. Don't promise anything else.
6. NEVER add hype/marketing language ("transform your life", "unlock", "discover", "imagine", "ready to", "this lifestyle", "change your life", "game-changer", "level up", "empower", "take control", "POV:", "What if I told you…").
7. NEVER add explanatory tails the original didn't have. NEVER add "context" the original didn't have. NEVER smooth out a sentence fragment, run-on, or casual aside — those are the viral fingerprints.
8. If the original script contains the user's niche already, keep it. If not, swap the niche/product/persona references but keep the underlying mechanic (e.g. a script about "saving for a house" can become a script about "moving abroad" if the structure is "I-was-stuck-then-realised-X-and-now-Y").
9. Output STRICT JSON only. No preamble.`;

export async function analyzeAndAdaptScript({
  apiKey,
  pastedScript,
  niche = "",
  audience = "",
  offer = "",
  ctaSentence = "",      // the locked CTA pulled from the CTA Control Panel (post-aware)
  ctaKeyword = "GO",
  strength = "balanced", // minimal | balanced | aggressive
}) {
  if (!apiKey) throw new Error("Missing OpenAI API key");
  const raw = (pastedScript || "").toString().trim();
  if (raw.length < 30) throw new Error("Paste a longer script (at least a couple of sentences).");
  if (raw.length > 6000) throw new Error("Script too long. Trim to ≤ ~6,000 characters.");

  const strengthKey = ["minimal", "balanced", "aggressive"].includes(strength) ? strength : "balanced";
  const strengthInstruction = STRENGTH_LABELS[strengthKey];

  const ctaLine = (ctaSentence || "").trim();
  const cta = ctaLine || `If you type ${(ctaKeyword || "GO").toUpperCase()}, I'll send breakdown so you can build remote income.`;

  const system = HARD_RULES;

  const user = `ADAPT THIS VIRAL SCRIPT.

TRANSFORMATION STRENGTH: ${strengthKey.toUpperCase()}
${strengthInstruction}

USER CONTEXT:
- Niche: ${niche || "(unspecified)"}
- Audience: ${audience || "(unspecified)"}
- Offer / product (CONTEXT ONLY — never named or pitched in the script): ${offer || "(unspecified)"}

LOCKED CTA (must replace the original CTA at the end of the adapted script, verbatim):
"""
${cta}
"""

ORIGINAL VIRAL SCRIPT:
"""
${raw}
"""

TASK:
1. Identify the ONE thing that most likely made the original go viral (hook archetype + retention mechanic + emotional payoff).
2. Adapt the script to the user's niche/audience using the chosen Transformation Strength.
3. Preserve the original hook structure, pacing, sentence cadence, and emotional curve.
4. End the adapted script with the LOCKED CTA above — placed at the same beat where the original CTA appeared. Match the energy level (soft / medium / hard) of the original.

Return STRICT JSON:
{
  "original_hook": "the first 1-2 sentences of the original — exactly as pasted (verbatim, no edits)",
  "viral_analysis": {
    "hook_archetype": "one short label — e.g. 'curiosity gap', 'contrarian truth', 'pattern interrupt', 'POV', 'list of N', 'before/after', 'common mistake', 'question'",
    "retention_mechanic": "1 short sentence — the mechanic that keeps people watching (e.g. 'open loop closed only at the end', 'rapid escalation', 'cliffhanger before each beat', 'specific number teased upfront')",
    "emotional_arc": "1 short sentence — the arc the viewer feels (e.g. 'frustration → recognition → relief', 'curiosity → contradiction → payoff')",
    "why_it_worked": "1-2 sentences — the reason this script earned views, written like an analyst's note",
    "energy_level": "soft" | "medium" | "hard"
  },
  "adapted_script": "the adapted spoken script. ONE continuous paragraph. Preserves hook structure + cadence. Ends with the LOCKED CTA above (verbatim). 80-180 words.",
  "cta_injected_version": "the same adapted_script with the locked CTA wrapped in <<<>>> markers so the user can see exactly where it lives. Example: '...regular sentence... <<<If you type GO, I\\'ll send breakdown so you can build remote income.>>>'"
}
JSON only.`;

  const result = await chatJSON({ apiKey, system, user, temperature: 0.85 });

  // Sanitise + validate.
  const out = {
    original_hook: cleanString(result?.original_hook),
    viral_analysis: {
      hook_archetype:    cleanString(result?.viral_analysis?.hook_archetype),
      retention_mechanic: cleanString(result?.viral_analysis?.retention_mechanic),
      emotional_arc:      cleanString(result?.viral_analysis?.emotional_arc),
      why_it_worked:      cleanString(result?.viral_analysis?.why_it_worked),
      energy_level:       cleanString(result?.viral_analysis?.energy_level || "medium"),
    },
    adapted_script:        cleanString(result?.adapted_script),
    cta_injected_version:  cleanString(result?.cta_injected_version),
    strength: strengthKey,
    cta_used: cta,
  };

  if (!out.adapted_script) throw new Error("No adapted script returned");

  // Force-inject the CTA at the end if the model dropped it.
  if (!out.adapted_script.toLowerCase().includes(cta.slice(0, 24).toLowerCase())) {
    out.adapted_script = `${out.adapted_script.replace(/[.!?]?\s*$/, "")} ${cta}`;
  }
  if (!out.cta_injected_version || !out.cta_injected_version.includes("<<<")) {
    out.cta_injected_version = out.adapted_script.replace(cta, `<<<${cta}>>>`);
  }

  // Soft validation — warn (don't block) if CTA looks invalid for the user's keyword.
  out.cta_valid = ctaIsValid(cta, ctaKeyword) || /comment\s+[A-Z]+/i.test(cta);

  return out;
}

function cleanString(s) {
  if (s == null) return "";
  return String(s).replace(/^\s+|\s+$/g, "");
}
